// 公開版の確認と更新案内の判定（docs/daytime_deployment_technical_design.md 6 章）。
// React や urql に依存しない純粋な判定だけを置く（supplier-frontend にも同じ内容がある）。

export type ReleaseInfo = { generation: number; buildId: string; publishedAt: string | null };

// 公開版情報 JSON（/.well-known/nitoel-release.json）から自分の frontend の値だけを取り出す。
// 形が崩れていれば null を返し、更新を推測しない。
export const parseReleaseInfo = (json: unknown, frontend: string): ReleaseInfo | null => {
  if (!json || typeof json !== "object") return null;
  const root = json as { generation?: unknown; frontends?: Record<string, { buildId?: unknown; publishedAt?: unknown }> };
  const entry = root.frontends?.[frontend];
  if (typeof root.generation !== "number" || !entry || typeof entry.buildId !== "string") return null;
  return {
    generation: root.generation,
    buildId: entry.buildId,
    publishedAt: typeof entry.publishedAt === "string" ? entry.publishedAt : null,
  };
};

export type UpdateDecision = "none" | "reload" | "notify";

export type UpdateInput = {
  currentBuildId: string;
  publishedBuildId: string | null;
  hasUnsaved: boolean;
  inFlight: boolean;
  autoUpdateSupported: boolean;
  // sessionStorage に残した「この公開版へ自動更新した」記録。同じ版へ 2 回目の自動更新はしない
  autoReloadedTo: string | null;
};

// 公開 build ID と違うときだけ動く。未保存入力・実行中処理・未対応画面では入力を消さないよう案内だけにする。
// 公開版の再確認で切り戻し（古い build ID へ戻る）にも追従する。
export const decideUpdate = (input: UpdateInput): UpdateDecision => {
  const { currentBuildId, publishedBuildId } = input;
  if (!publishedBuildId || currentBuildId === "unknown" || currentBuildId === publishedBuildId) return "none";
  if (input.hasUnsaved || input.inFlight || !input.autoUpdateSupported) return "notify";
  if (input.autoReloadedTo === publishedBuildId) return "notify";
  return "reload";
};

export type ApiErrorClass = "stale-contract" | "incident" | "business" | "session";

export type ApiErrorInput = {
  clientBuildId: string;
  publishedBuildId: string | null;
  status: number;
  graphQLErrors: { message: string; extensions?: { code?: unknown } }[];
  networkError?: boolean;
};

// graphql-ruby の静的検証（削除・改名された field / 引数 / 型）のエラーコード。
// 旧画面が新 API に古い operation を送ったときに出る
const CONTRACT_ERROR_CODES = new Set([
  "undefinedField",
  "undefinedType",
  "argumentNotAccepted",
  "missingRequiredArguments",
  "argumentLiteralsIncompatible",
  "variableMismatch",
  "selectionMismatch",
  "fragmentTypeMismatch",
]);

// 旧 build の ID だけでは想定内と断定しない。公開版より古い画面が、契約エラーだけを受けたときに限って
// 旧画面のエラーとする。内部例外・通信障害・原因不明は旧版でも障害として扱う（設計 6.3）。
export const classifyApiError = (input: ApiErrorInput): ApiErrorClass => {
  if (input.status === 401) return "session";
  if (input.networkError || input.status >= 500 || input.status === 0) return "incident";
  const errors = input.graphQLErrors;
  if (errors.length === 0) return "incident";
  const contract = errors.filter((e) => typeof e.extensions?.code === "string" && CONTRACT_ERROR_CODES.has(e.extensions.code));
  if (contract.length === 0) return "business";
  const stale =
    input.publishedBuildId !== null && input.clientBuildId !== "unknown" && input.clientBuildId !== input.publishedBuildId;
  return stale && contract.length === errors.length ? "stale-contract" : "incident";
};

export type ReleaseEvent = "saved" | "stale-contract";

export type ReleaseSnapshot = {
  hasUnsaved: boolean;
  inFlight: boolean;
  autoUpdateSupported: boolean;
  publishedBuildId: string | null;
};

// 未保存入力・実行中処理・自動更新対応の登録先。画面と urql exchange の両方から使うので React の外に置く。
export const createReleaseStore = () => {
  const unsaved = new Set<string>();
  let inFlight = 0;
  let autoUpdate = 0;
  let publishedBuildId: string | null = null;
  const listeners = new Set<(event: ReleaseEvent) => void>();

  return {
    setUnsaved(key: string, dirty: boolean) {
      if (dirty) unsaved.add(key);
      else unsaved.delete(key);
    },
    beginRequest() {
      inFlight += 1;
      let ended = false;
      return () => {
        if (ended) return;
        ended = true;
        inFlight -= 1;
      };
    },
    supportAutoUpdate() {
      autoUpdate += 1;
      let released = false;
      return () => {
        if (released) return;
        released = true;
        autoUpdate -= 1;
      };
    },
    // 直近に確認できた公開版。API エラーの分類（旧画面か）に使う
    setPublished(buildId: string | null) {
      publishedBuildId = buildId;
    },
    snapshot(): ReleaseSnapshot {
      return { hasUnsaved: unsaved.size > 0, inFlight: inFlight > 0, autoUpdateSupported: autoUpdate > 0, publishedBuildId };
    },
    emit(event: ReleaseEvent) {
      listeners.forEach((listener) => listener(event));
    },
    subscribe(listener: (event: ReleaseEvent) => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export type ReleaseStore = ReturnType<typeof createReleaseStore>;
